import { useEffect, useRef, useState } from 'react'
import {
  AlertTriangle,
  Film,
  LoaderCircle,
  Pause,
  Play,
  Scissors,
  Volume2,
} from 'lucide-react'
import type { ToolDefinition, ToolParams } from '../types'
import type { Locale } from '../i18n'

type Props = {
  file: File
  fileCount: number
  tool: ToolDefinition
  params: ToolParams
  locale: Locale
  onParamChange: (key: string, value: string | number) => void
  onDuration: (fileKey: string, duration: number) => void
}

function isVideo(file: File): boolean {
  return file.type.startsWith('video/') || /\.(mp4|webm|mov|m4v|ogv|mkv|avi)$/i.test(file.name)
}

export function formatMediaTime(value: number, includeMillis = false): string {
  const safe = Number.isFinite(value) ? Math.max(0, value) : 0
  const totalCentiseconds = Math.round(safe * 100)
  const hours = Math.floor(totalCentiseconds / 360000)
  const minutes = Math.floor((totalCentiseconds % 360000) / 6000)
  const seconds = Math.floor((totalCentiseconds % 6000) / 100)
  const base = [hours, minutes, seconds].map((part) => String(part).padStart(2, '0')).join(':')
  return includeMillis ? `${base}.${String(totalCentiseconds % 100).padStart(2, '0')}` : base
}

export function clampTrimStart(value: number, end: number): number {
  return Number(Math.min(Math.max(0, value), Math.max(0, end - 0.04)).toFixed(2))
}

export function clampTrimEnd(value: number, start: number, duration: number): number {
  return Number(
    Math.max(Math.min(duration, value), Math.min(duration, start + 0.04)).toFixed(2),
  )
}

function waitFor(video: HTMLVideoElement, eventName: 'loadedmetadata' | 'seeked'): Promise<void> {
  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      cleanup()
      reject(new Error(`Timed out waiting for ${eventName}`))
    }, 8000)
    const cleanup = () => {
      window.clearTimeout(timeout)
      video.removeEventListener(eventName, done)
      video.removeEventListener('error', failed)
    }
    const done = () => {
      cleanup()
      resolve()
    }
    const failed = () => {
      cleanup()
      reject(new Error('Video preview is not supported'))
    }
    video.addEventListener(eventName, done, { once: true })
    video.addEventListener('error', failed, { once: true })
  })
}

async function createThumbnails(source: string, duration: number): Promise<string[]> {
  const captureVideo = document.createElement('video')
  captureVideo.preload = 'auto'
  captureVideo.muted = true
  captureVideo.playsInline = true
  captureVideo.src = source

  try {
    if (captureVideo.readyState < 1) await waitFor(captureVideo, 'loadedmetadata')
    const canvas = document.createElement('canvas')
    canvas.width = 160
    canvas.height = 90
    const context = canvas.getContext('2d')
    if (!context) return []

    const frames: string[] = []
    const count = duration < 8 ? 6 : 10
    for (let index = 0; index < count; index += 1) {
      captureVideo.currentTime = Math.min(
        Math.max(0, duration - 0.05),
        duration * ((index + 0.5) / count),
      )
      await waitFor(captureVideo, 'seeked')
      context.drawImage(captureVideo, 0, 0, canvas.width, canvas.height)
      frames.push(canvas.toDataURL('image/jpeg', 0.58))
    }
    return frames
  } finally {
    captureVideo.removeAttribute('src')
    captureVideo.load()
  }
}

export default function MediaPreview({
  file,
  fileCount,
  tool,
  params,
  locale,
  onParamChange,
  onDuration,
}: Props) {
  const [sourceURL, setSourceURL] = useState('')
  const [duration, setDuration] = useState(0)
  const [currentTime, setCurrentTime] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const [previewError, setPreviewError] = useState(false)
  const [thumbnails, setThumbnails] = useState<string[]>([])
  const [isCapturing, setIsCapturing] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)
  const isTrim = tool.id === 'video-trim'
  const copy = {
    en: {
      section: 'Video preview', first: 'First video preview', source: 'Source preview',
      trimHint: 'Drag the timeline handles to move the picture to each cut point',
      previewHint: 'The local preview does not upload or copy your file',
      pause: 'Pause preview', play: 'Play preview', unsupported: 'This codec cannot be previewed by the browser',
      unsupportedHint: 'FFmpeg will still try to process it. Convert to MP4 (H.264) first if processing fails.',
      cut: 'Trim position', drag: 'Drag the white handles to set the kept range', keep: 'Keep',
      start: 'Start', end: 'End', audio: 'Keep audio', noAudio: 'Remove audio',
    },
    zh: {
      section: '视频预览', first: '首个视频预览', source: '源文件预览',
      trimHint: '拖动时间轴把手，画面会同步定位到剪辑点',
      previewHint: '本地预览不会上传或复制文件',
      pause: '暂停预览', play: '播放预览', unsupported: '浏览器无法直接预览这个编码',
      unsupportedHint: 'FFmpeg 仍会尝试处理；如失败，请先转换为 MP4（H.264）。',
      cut: '剪辑位置', drag: '拖动白色把手设置保留区间', keep: '保留',
      start: '开始', end: '结束', audio: '保留音频', noAudio: '不保留音频',
    },
    ja: {
      section: '動画プレビュー', first: '先頭動画のプレビュー', source: '元ファイルのプレビュー',
      trimHint: 'タイムラインのハンドルをドラッグすると各カット位置へ移動します',
      previewHint: 'ローカルプレビューではファイルをアップロード・複製しません',
      pause: 'プレビューを一時停止', play: 'プレビューを再生', unsupported: 'このコーデックはブラウザでプレビューできません',
      unsupportedHint: 'FFmpeg は処理を試行します。失敗した場合は MP4（H.264）へ変換してください。',
      cut: 'トリミング位置', drag: '白いハンドルで保持範囲を設定', keep: '保持',
      start: '開始', end: '終了', audio: '音声を保持', noAudio: '音声を削除',
    },
  }[locale]

  useEffect(() => {
    if (!isVideo(file)) return
    const nextURL = URL.createObjectURL(file)
    setSourceURL(nextURL)
    setDuration(0)
    setCurrentTime(0)
    setPreviewError(false)
    setThumbnails([])
    return () => URL.revokeObjectURL(nextURL)
  }, [file])

  useEffect(() => {
    if (!isTrim || !sourceURL || !duration || previewError) return
    let active = true
    setIsCapturing(true)
    createThumbnails(sourceURL, duration)
      .then((frames) => {
        if (active) setThumbnails(frames)
      })
      .catch(() => {
        if (active) setThumbnails([])
      })
      .finally(() => {
        if (active) setIsCapturing(false)
      })
    return () => {
      active = false
    }
  }, [duration, isTrim, previewError, sourceURL])

  useEffect(() => {
    if (tool.id !== 'video-screenshot' || !duration) return
    const seekTime = Math.min(duration, Math.max(0, Number(params.time)))
    if (videoRef.current && Math.abs(videoRef.current.currentTime - seekTime) > 0.05) {
      videoRef.current.currentTime = seekTime
    }
  }, [duration, params.time, tool.id])

  if (!isVideo(file) || !sourceURL) return null

  const start = Math.min(duration, Math.max(0, Number(params.start) || 0))
  const end = Math.min(duration || Number(params.end) || 0, Math.max(start, Number(params.end) || 0))
  const startPercent = duration ? (start / duration) * 100 : 0
  const endPercent = duration ? (end / duration) * 100 : 100
  const playheadPercent = duration ? (currentTime / duration) * 100 : 0
  const fileKey = `${file.name}:${file.size}:${file.lastModified}`

  const seek = (time: number) => {
    const next = Math.min(duration, Math.max(0, time))
    setCurrentTime(next)
    if (videoRef.current) videoRef.current.currentTime = next
  }

  const updateStart = (value: number) => {
    const next = clampTrimStart(value, end)
    onParamChange('start', next)
    seek(next)
  }

  const updateEnd = (value: number) => {
    const next = clampTrimEnd(value, start, duration)
    onParamChange('end', next)
    seek(next)
  }

  const togglePlayback = async () => {
    const video = videoRef.current
    if (!video) return
    if (video.paused) {
      if (isTrim && video.currentTime >= end - 0.04) video.currentTime = start
      await video.play()
    } else {
      video.pause()
    }
  }

  return (
    <section className="media-preview-section" aria-label={copy.section}>
      <div className="section-heading preview-section-heading">
        <Film size={18} />
        <div>
          <h3>{fileCount > 1 ? copy.first : copy.source}</h3>
          <p>{isTrim ? copy.trimHint : copy.previewHint}</p>
        </div>
        {duration > 0 && <span className="preview-duration">{formatMediaTime(duration)}</span>}
      </div>

      <div className="source-preview">
        <video
          ref={videoRef}
          src={sourceURL}
          controls={!isTrim}
          playsInline
          preload="metadata"
          onLoadedMetadata={(event) => {
            const nextDuration = event.currentTarget.duration
            if (!Number.isFinite(nextDuration) || nextDuration <= 0) return
            setDuration(nextDuration)
            onDuration(fileKey, nextDuration)
          }}
          onTimeUpdate={(event) => {
            const nextTime = event.currentTarget.currentTime
            setCurrentTime(nextTime)
            if (isTrim && nextTime >= end && !event.currentTarget.paused) {
              event.currentTarget.pause()
              event.currentTarget.currentTime = start
            }
          }}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onError={() => setPreviewError(true)}
        />
        {isTrim && !previewError && (
          <button className="preview-play-button" type="button" onClick={togglePlayback} aria-label={isPlaying ? copy.pause : copy.play}>
            {isPlaying ? <Pause size={23} fill="currentColor" /> : <Play size={24} fill="currentColor" />}
          </button>
        )}
        <span className="preview-clock">{formatMediaTime(currentTime)} / {formatMediaTime(duration)}</span>
        {previewError && (
          <div className="preview-error">
            <AlertTriangle size={25} />
            <strong>{copy.unsupported}</strong>
            <span>{copy.unsupportedHint}</span>
          </div>
        )}
      </div>

      {isTrim && duration > 0 && !previewError && (
        <div className="trim-editor">
          <div className="trim-heading">
            <div>
              <Scissors size={17} />
              <span><strong>{copy.cut}</strong><small>{copy.drag}</small></span>
            </div>
            <span className="trim-length">{copy.keep} {formatMediaTime(Math.max(0, end - start), true)}</span>
          </div>

          <div
            className="filmstrip"
            role="presentation"
            onClick={(event) => {
              const rect = event.currentTarget.getBoundingClientRect()
              seek(duration * ((event.clientX - rect.left) / rect.width))
            }}
          >
            <div className="thumbnail-grid" style={{ gridTemplateColumns: `repeat(${Math.max(1, thumbnails.length)}, 1fr)` }}>
              {thumbnails.length > 0
                ? thumbnails.map((thumbnail, index) => (
                    <span key={`${thumbnail.slice(-20)}-${index}`} style={{ backgroundImage: `url(${thumbnail})` }} />
                  ))
                : <span className="thumbnail-loading">{isCapturing ? <LoaderCircle size={18} /> : <Film size={18} />}</span>}
            </div>
            <span className="trim-shade shade-start" style={{ width: `${startPercent}%` }} />
            <span className="trim-shade shade-end" style={{ width: `${100 - endPercent}%` }} />
            <span className="trim-selection" style={{ left: `${startPercent}%`, width: `${Math.max(0, endPercent - startPercent)}%` }} />
            <span className="timeline-playhead" style={{ left: `${playheadPercent}%` }} />
            <input
              className="timeline-range timeline-start"
              type="range"
              aria-label={copy.start}
              min={0}
              max={duration}
              step={0.01}
              value={start}
              onChange={(event) => updateStart(Number(event.target.value))}
            />
            <input
              className="timeline-range timeline-end"
              type="range"
              aria-label={copy.end}
              min={0}
              max={duration}
              step={0.01}
              value={end}
              onChange={(event) => updateEnd(Number(event.target.value))}
            />
          </div>

          <div className="timeline-ticks">
            <span>{formatMediaTime(0)}</span>
            <span>{formatMediaTime(duration / 3)}</span>
            <span>{formatMediaTime((duration * 2) / 3)}</span>
            <span>{formatMediaTime(duration)}</span>
          </div>

          <div className="trim-time-fields">
            <label>
              <span>{copy.start}</span>
              <input
                type="number"
                min={0}
                max={Math.max(0, end - 0.04)}
                step={0.01}
                value={start}
                onChange={(event) => updateStart(Number(event.target.value))}
              />
              <small>{formatMediaTime(start, true)}</small>
            </label>
            <span className="trim-arrow">→</span>
            <label>
              <span>{copy.end}</span>
              <input
                type="number"
                min={Math.min(duration, start + 0.04)}
                max={duration}
                step={0.01}
                value={end}
                onChange={(event) => updateEnd(Number(event.target.value))}
              />
              <small>{formatMediaTime(end, true)}</small>
            </label>
            <div className="trim-audio-state">
              <Volume2 size={16} />
              <span>{params.keepAudio === 'no' ? copy.noAudio : copy.audio}</span>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}
